import { format, isToday, isTomorrow, isYesterday, parseISO } from 'date-fns'

/**
 * Format date for display
 */
export function formatDate(dateString) {
  if (!dateString) return ''
  
  const date = typeof dateString === 'string' ? parseISO(dateString) : dateString
  
  if (isToday(date)) return 'Today'
  if (isTomorrow(date)) return 'Tomorrow'
  if (isYesterday(date)) return 'Yesterday'
  
  return format(date, 'MMM d, yyyy')
}

/**
 * Format time for display
 */
export function formatTime(timeString) {
  if (!timeString) return ''
  
  const [hours, minutes] = timeString.split(':')
  const date = new Date()
  date.setHours(parseInt(hours), parseInt(minutes))
  
  return format(date, 'h:mm a')
}

/**
 * Format date and time together
 */
export function formatDateTime(dateString, timeString) {
  const date = formatDate(dateString)
  const time = formatTime(timeString)
  
  if (time) {
    return `${date} at ${time}`
  }
  return date
}

/**
 * Get relative time description
 */
export function getRelativeTime(dateString, timeString) {
  if (!dateString) return ''
  
  const date = typeof dateString === 'string' ? parseISO(dateString) : dateString
  const now = new Date()
  
  // Combine date and time if provided
  if (timeString) {
    const [hours, minutes] = timeString.split(':')
    date.setHours(parseInt(hours), parseInt(minutes))
  }
  
  const diffMs = date - now
  const diffMins = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  
  if (diffMins < 0) return 'Overdue'
  if (diffMins < 60) return `in ${diffMins} min`
  if (diffHours < 24) return `in ${diffHours} hour${diffHours > 1 ? 's' : ''}`
  if (diffDays === 1) return 'Tomorrow'
  if (diffDays < 7) return `in ${diffDays} days`
  
  return formatDate(dateString)
}

/**
 * Check if task is overdue
 */
export function isOverdue(dateString, timeString) {
  if (!dateString) return false
  
  const date = typeof dateString === 'string' ? parseISO(dateString) : dateString
  
  if (timeString) {
    const [hours, minutes] = timeString.split(':')
    date.setHours(parseInt(hours), parseInt(minutes))
  } else {
    date.setHours(23, 59, 59)
  }
  
  return date < new Date()
}

/**
 * Get priority color class
 */
export function getPriorityColor(priority) {
  switch (priority) {
    case 'high':
      return 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-300'
    case 'medium':
      return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-300'
    case 'low':
      return 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-300'
    default:
      return 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-300'
  }
}

/**
 * Get status color class
 */
export function getStatusColor(status) {
  switch (status) {
    case 'completed':
      return 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-300'
    case 'in_progress':
      return 'text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300'
    case 'pending':
      return 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-300'
    default:
      return 'text-gray-600 bg-gray-100'
  }
}

/**
 * Get category icon/color
 */
export function getCategoryStyle(category) {
  const styles = {
    work: { color: 'text-blue-600', bg: 'bg-blue-100', icon: '💼' },
    personal: { color: 'text-purple-600', bg: 'bg-purple-100', icon: '🏠' },
    health: { color: 'text-green-600', bg: 'bg-green-100', icon: '💪' },
    shopping: { color: 'text-orange-600', bg: 'bg-orange-100', icon: '🛒' },
    general: { color: 'text-gray-600', bg: 'bg-gray-100', icon: '📝' },
  }
  
  return styles[category] || styles.general
}